import React, { FunctionComponent, useState,useEffect } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  InputBase,
  IconButton,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { IMAGES } from "../../utils/constants";
import { useUserLogout } from "../../hooks/api/auth";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { setLogout } from "../../store/slices/auth";
import { RootState } from "../../store";
import moment from "moment";
import { useGetChannelPartnerOEntryHeaderCart} from "../../hooks/api/oms";
import { Badge } from "@mui/material";
import CartIcon from '../../assets/img/Online Order Entry.png';
import EditOutlinedIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { useQueryClient } from '@tanstack/react-query';
import StorageService from '../../services/storageService';

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #cfcdcd',
  marginLeft: theme.spacing(2),
  width: "300px",
  display: "flex",
  alignItems: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  padding: theme.spacing(1, 2, 1, 2),
  paddingRight: `calc(1em + ${theme.spacing(4)})`,
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: "58%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  color: "#d01e42",
}));

const Header: FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const userInfo = useSelector((state: RootState) => state.auth.user);
  const queryClient = useQueryClient();

  const { mutateAsync: getUserLogout } = useUserLogout();
  const { data: oentryheader } = useGetChannelPartnerOEntryHeaderCart();
  const selectedExtranet = StorageService.getSelectedExtranet();

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  let timeoutId: NodeJS.Timeout | null = null;

  const handleClose = () => {
    if (!!timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      setAnchorEl(null);
    }, 0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuEnter = () => {
    if (!!timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  const handleLogout = async () => {
    try {
      await getUserLogout({});
      dispatch(setLogout());
      
      // Clear all caches to prevent menu persistence
      queryClient.clear();
      sessionStorage.clear();
      // Clear only auth data, preserve order entry data
      StorageService.clearAuthOnly();
      
      // Navigate to login first
      navigate('/login');
      
      // Force page reload to ensure clean state
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if API call fails, clear local state
      dispatch(setLogout());
      queryClient.clear();
      sessionStorage.clear();
      StorageService.clearAuthOnly();
      navigate('/login');
      window.location.reload();
    }
  };
  const initials = userInfo?.username?.split(' ')?.filter((word: string) => word.length > 0)?.map((word: string) => word[0].toUpperCase())?.join('');

  return (
    <AppBar position="static" elevation={0} sx={{ backgroundColor: "#f9fcfd", color: "black" }}>
      <Toolbar sx={{ justifyContent: "space-between", px: 2, height: 75 }}>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* <a href="https://www.barksdale.com/" target="_blank" rel="noopener noreferrer">
            <img src={IMAGES.barksdaleLogo} alt="Logo" height={50} />
          </a> */}
          <Link to="/dashboard">
            <img src={IMAGES.barksdaleLogo} alt="Logo" height={50} />
          </Link>

          <Box sx={{ width: "2px", height: 40, backgroundColor: "#d01e42", mx: 1 }} />

          <img src={IMAGES.extranetLogo} alt="Logo" height={50} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 3 }}>
            <Typography variant="h6"></Typography>

            <Search sx={{ display: 'none' }}>
              <StyledInputBase placeholder="Search" disabled />
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
            </Search>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box>
            <Typography variant="caption">Last login time</Typography>
            <Typography variant="body2">{userInfo?.lastLogin ? moment(userInfo.lastLogin).format('MM-DD-YYYY HH:mm:ss') : 'N/A'}</Typography>
          </Box>

          <IconButton>
            <HelpOutlineIcon />
          </IconButton>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#d01e42",
              borderRadius: "10px",
              minWidth: "40px",
              width: "40px",
              height: "40px",
              fontWeight: "bold",
              color: "white",
              p: 0,
            }}
          >
            {initials}
          </Button>

          <Button
            variant="contained"
            onClick={handleOpen}
            onMouseEnter={handleOpen}
            onMouseLeave={handleClose}
            sx={{
              zIndex: (theme) => theme.zIndex.modal + 1,
              backgroundColor: "transparent",
              boxShadow: 0,
              borderRadius: "10px",
              minWidth: "40px",
              height: "40px",
              fontWeight: "bold",
              color: "#000000",
              p: 0,
              "&:hover": {
                backgroundColor: "transparent",
                boxShadow: 0,
              }
            }}
          >
            <Typography sx={{ mr: 1 }}>
              {userInfo?.username && userInfo.username.length > 12
                ? userInfo.username.slice(0, 12) + '...'
                : userInfo?.username || 'User'}
            </Typography>
            <ExpandMoreOutlinedIcon />
          </Button>
          
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              onMouseLeave: handleMenuClose,
              onMouseEnter: handleMenuEnter
            }}>
            {selectedExtranet !== 'Barksdale Admin' && (
              <MenuItem onClick={() => {
                // Smart Cart Navigation Logic:
                // - If cart has 0 items → Navigate to order entry home page (to start new order)
                // - If cart has 1+ items → Navigate to order page (to view/edit existing cart)
                const cartItemCount = oentryheader?.totalItems || 0;
                if (cartItemCount === 0) {
                  navigate('/order-management-system/online-order-entry');
                } else {
                  navigate('/order-management-system/online-order-entry/order');
                }
              }}>
                <Badge
                  badgeContent={oentryheader?.totalItems || 0}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      right: -4,
                      top: 4,
                      padding: '0 4px',
                      fontSize: '0.75rem',
                      minWidth: '18px',
                      height: '18px',
                    },
                  }}
                  invisible={!oentryheader?.totalItems}
                >
                                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <img src={CartIcon} alt="Cart" width={18} height={18} />
                     Cart
                   </Box>
                </Badge>
              </MenuItem>
            )}

            <MenuItem onClick={() => navigate(`/profile/edit`)}> 
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EditOutlinedIcon fontSize="small" />
                Edit Profile
              </Box>
            </MenuItem>

            <MenuItem onClick={handleLogout}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LogoutIcon fontSize="small" />
                Logout
              </Box>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
